/**
 * The modal-surface host — renders whichever generative-UI surface the
 * {@link SurfaceStore} has open inside a focus-trapped `@4f/ui` `Modal`. One
 * renderer per {@link UiSurface} (OCP: a new surface = a new `<Match>`, no fork of
 * the others). The builder, billing summary, referral dashboard, and settings
 * all ship here; the `PlaceholderSurface` fallback keeps any future surface added
 * to the enum before its renderer lands graceful. The `modal` directive's
 * `title` + `dismissible` hints drive the chrome.
 */
import type { UiSurface } from '@4f/shared';
import { Button, Icon, Modal, useT } from '@4f/ui';
import { type JSX, Match, Show, Switch } from 'solid-js';
import type { CompanionView } from '../../api/types.ts';
import type { BillingStore } from '../../stores/billing.ts';
import type { BuilderStore } from '../../stores/builder.ts';
import type { DataRightsStore } from '../../stores/data-rights.ts';
import type { PrebuiltStore } from '../../stores/prebuilt.ts';
import type { ReferralStore } from '../../stores/referral.ts';
import type { SettingsStore } from '../../stores/settings.ts';
import type { SurfaceStore } from '../../stores/surface.ts';
import { BillingSurface } from '../billing/BillingSurface.tsx';
import { BuilderSurface } from '../builder/BuilderSurface.tsx';
import type { BuilderChoice } from '../builder/builder-picker.ts';
import { ReferralSurface } from '../referral/ReferralSurface.tsx';
import { SettingsSurface } from './SettingsSurface.tsx';

const TITLE_ID = 'f4-surface-modal-title';

export interface SurfaceHostProps {
  readonly surface: SurfaceStore;
  readonly builder: BuilderStore;
  /** The free ready-made catalog behind the builder's prebuilt door. */
  readonly prebuilt: PrebuiltStore;
  readonly billing: BillingStore;
  readonly referral: ReferralStore;
  readonly settings: SettingsStore;
  /** The self-serve export/delete store the settings surface renders. */
  readonly dataRights: DataRightsStore;
  /** Renders an ISO instant in the user's own timezone (the shell's formatter). */
  formatDate(iso: string): string;
  /** Ends the session — offered after a deletion request, on the user's click. */
  onSignOut(): void;
  /** The app's own origin (`location.origin`) — the referral link builder. */
  readonly origin: string;
  /** Wired by the shell — reload the switcher, open her thread, toast, close. */
  onCompanionCreated(companion: CompanionView): void;
  /** Wired by the shell — route the picked funnel door (custom → billing first). */
  onBuilderChoose(choice: BuilderChoice): void;
}

/** The heading for a surface — the directive's override, else the surface's own title. */
function surfaceTitle(
  t: (key: string) => string,
  open: { surface: UiSurface; title?: string },
): string {
  if (open.title) {
    return open.title;
  }
  return open.surface === 'builder' ? t('builder.title') : t(`surface.surfaces.${open.surface}`);
}

export function SurfaceHost(props: SurfaceHostProps): JSX.Element {
  const { t } = useT();

  const close = (): void => props.surface.close();
  // A non-dismissible flow (a required step) ignores Escape / scrim clicks.
  const requestClose = (): void => {
    if (props.surface.active()?.dismissible !== false) {
      close();
    }
  };

  return (
    <Show when={props.surface.active()}>
      {(open) => (
        <Modal
          open
          onClose={requestClose}
          closeLabel={t('surface.close')}
          labelledBy={TITLE_ID}
          class="f4-surface-modal"
        >
          <header class="f4-surface-modal__header">
            <h2 id={TITLE_ID} class="f4-genui__title">
              {surfaceTitle(t, open())}
            </h2>
            <Show when={open().dismissible !== false}>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                aria-label={t('surface.close')}
                onClick={close}
              >
                <Icon name="close" />
              </Button>
            </Show>
          </header>

          <Switch fallback={<PlaceholderSurface name={surfaceTitle(t, open())} />}>
            <Match when={open().surface === 'builder'}>
              <BuilderSurface
                store={props.builder}
                prebuilt={props.prebuilt}
                onCreated={props.onCompanionCreated}
                onChoose={props.onBuilderChoose}
              />
            </Match>
            <Match when={open().surface === 'billing'}>
              <BillingSurface store={props.billing} />
            </Match>
            <Match when={open().surface === 'referral'}>
              <ReferralSurface store={props.referral} origin={props.origin} />
            </Match>
            <Match when={open().surface === 'settings'}>
              <SettingsSurface
                store={props.settings}
                dataRights={props.dataRights}
                formatDate={props.formatDate}
                onSignOut={props.onSignOut}
              />
            </Match>
          </Switch>
        </Modal>
      )}
    </Show>
  );
}

/** Graceful stand-in for a surface whose generative UI has not shipped yet. */
function PlaceholderSurface(props: { readonly name: string }): JSX.Element {
  const { t } = useT();
  return <p class="f4-genui__prompt">{t('surface.comingSoon', { surface: props.name })}</p>;
}
